import { IterUtils } from "../../../../../Common/IterableUtils";
import { isNotNullOrUndefined } from "../../../../../Common/TypingUtils";
import { FormSavingStatus } from "../../../Controls/FormParts/Panel/FormSaveStatus/Store/FormSaveStatusActions";
import { Deferred } from "../../../Helpers/Deferred";
import { runFloatingAsyncAction } from "../../../Helpers/TypingUtils";
import { setFormSaveStatus } from "../../ActionCreators/FormActionCreators";
import { ICandyApiDraftClient, IInnerChangeResult } from "../../../../../Common/CandyApiClient/ICandyApiClient";
import { FormStore } from "../Store/CandyStore";
import { ModelEvent } from "../EventManagement/ModelEvents/ModelEvent";
import { PathTokens } from "../../../../../Common/ModelPath/ModelPath";
import { uuid } from "../../../../../Common/UUID";
import { AbsoluteModelFieldPath } from "../../../../../Common/ModelPath/AbsoluteModelFieldPath";
import { IPublicForm } from "../../../Forms/PublicForm/IPublicForm";
import { IExternalEventManager } from "../EventManagement/ExternalEvents/ExternalEventManager";
import { formSavingStatusEvent } from "../EventManagement/ExternalEvents/ExternalEvents";
import { ServerChangeSet } from "../../../../../Common/CandyApiClient/Types/ChangeSet/ServerChangeSet";
import { waitPollingTask } from "../../../../../Common/CandyApiClient/Types/PollingTasks/PollingTaskWaiter";
import { ITrackActionProvider } from "../../Form/IForm";
import { SSC_REQUEST_EXECUTED, SSC_REQUEST_WITH_CALLBACK_DATA_EXECUTED } from "../../../Controls/metricsAction";

import { ChangeSet } from "./ChangeSet";
import { ChangesCallback, ConflictChangeSetError, IChangeSetManager, InnerChangingAction } from "./IChangeSetManager";

export interface SendChangeSetOptions {
    force?: boolean;
    operationName?: string;
}

export class ChangeSetManager implements IChangeSetManager {
    private readonly getStore: () => FormStore;
    private readonly useServerSideCalculations: boolean;
    private readonly onConflictDetected: () => void;
    private readonly candyApiDraftClient: ICandyApiDraftClient;
    private readonly attachmentsPublicForms: { [attachmentId: string]: IPublicForm };
    private readonly externalEventManager: IExternalEventManager;
    private readonly tracker: ITrackActionProvider;

    private readonly changeSetSendQueue: Array<[ChangeSet | InnerChangingAction, Deferred<void>]> = [];
    private readonly redefinedChangeSets: ChangeSet[] = [];
    private readonly serverChangesCallbacks: { [key: string]: ChangesCallback } = {};
    private changeSet: ChangeSet;
    private autosendIntervalHandler?: number = undefined;
    private processingRequest = false;
    private currentDataVersion = 0;
    private isInConflictState = false;

    public constructor(
        candyApiDraftClient: ICandyApiDraftClient,
        getStore: () => FormStore,
        onConflictDetected: () => void,
        useServerSideCalculations: boolean,
        attachmentsPublicForms: { [attachmentId: string]: IPublicForm },
        externalEventManager: IExternalEventManager,
        tracker: ITrackActionProvider
    ) {
        this.tracker = tracker;
        this.attachmentsPublicForms = attachmentsPublicForms;
        this.useServerSideCalculations = useServerSideCalculations;
        this.changeSet = new ChangeSet(this.useServerSideCalculations);
        this.onConflictDetected = onConflictDetected;
        this.candyApiDraftClient = candyApiDraftClient;
        this.externalEventManager = externalEventManager;
        this.getStore = getStore;
    }

    public applyEvents(events: readonly ModelEvent[]): void {
        const changeSet = this.getCurrentChangeSet();
        const isRealChangeSet = changeSet === this.changeSet;

        if (isRealChangeSet) {
            const canBeMerged = isRealChangeSet && events.every(evt => changeSet.canBeMerged(evt));
            if (!canBeMerged) {
                runFloatingAsyncAction(() => this.enqueueChangeSetToSend(this.changeSet));
                this.changeSet = new ChangeSet(this.useServerSideCalculations);
            }
        }

        for (const event of events) {
            this.getCurrentChangeSet().appendEventInfo(event);
        }

        if (events.length > 0) {
            if (
                this.redefinedChangeSets.length === 0 &&
                this.getStore().getState().formVisualState.savingStatus === "success"
            ) {
                this.setSavingStatus("unsavedChanges");
            }
        }
    }

    public scheduleInnerChangeAction(action: InnerChangingAction): Promise<void> {
        const promise = this.enqueueChangeSetToSend(action);
        if (!this.processingRequest) {
            runFloatingAsyncAction(() => this.processEnqueuedChangeSets(false));
        }
        return promise;
    }

    public readonly send = (options: SendChangeSetOptions = {}): Promise<void> => {
        const promise = this.enqueueChangeSetToSend(this.changeSet);
        this.changeSet = new ChangeSet(this.useServerSideCalculations);
        if (!this.processingRequest) {
            runFloatingAsyncAction(() => this.processEnqueuedChangeSets(options.force ?? false, options.operationName));
        }

        const attachmentsPromises = Object.values(this.attachmentsPublicForms).map(attachmentForm =>
            attachmentForm.save()
        );

        return promise.then(async _ => await Promise.all(attachmentsPromises)).then();
    };

    public sendSync(): void {
        if (this.changeSet.isEmpty()) {
            return;
        }
        if (this.processingRequest) {
            throw new Error("Cannot send ChangeSet: there are sending in progress");
        }

        if (this.isInConflictState) {
            throw new ConflictChangeSetError(`Conflict detected`);
        }

        this.stopAutosend();
        try {
            const status = this.candyApiDraftClient.sendChangeSetSync(
                this.changeSet.toServerChangeSet(),
                this.currentDataVersion
            );
            if (typeof status === "object") {
                this.setCurrentDataVersion(status.dataVersion);
            } else if (status === 409) {
                this.setConflictState();
                throw new ConflictChangeSetError(`Conflict detected`);
            } else if (status !== 200) {
                throw new Error(`Failed to save ChangeSet with status: ${status}`);
            }
            this.changeSet = new ChangeSet(this.useServerSideCalculations);
        } finally {
            this.startAutosend(this.useServerSideCalculations ? 500 : 10000);
        }
    }

    public startAutosend(sendInterval: number): void {
        if (this.autosendIntervalHandler != undefined) {
            this.stopAutosend();
        }

        const sendAction = () => {
            if (!this.processingRequest) {
                runFloatingAsyncAction(() => this.send());
            }
        };

        // @ts-ignore
        this.autosendIntervalHandler = setInterval(sendAction, sendInterval);
    }

    public stopAutosend(): void {
        if (this.autosendIntervalHandler == undefined) {
            return;
        }
        clearInterval(this.autosendIntervalHandler);
    }

    public redefine(): void {
        this.redefinedChangeSets.push(new ChangeSet(this.useServerSideCalculations));
    }

    public isRedefined(): boolean {
        return this.redefinedChangeSets.length > 0;
    }

    public dropRedefined(): void {
        this.redefinedChangeSets.pop();
    }

    public mergeRedefined(): void {
        const lastChangeSet = this.redefinedChangeSets.pop();
        if (lastChangeSet != undefined) {
            // NOTE Тут трудноуловимый баг: prependWithChangeSet добавляет в целевой данные, как если бы lastChangeSet,
            // шел перед целевым. Тут явно хотели другого
            this.getCurrentChangeSet().prependWithChangeSet(lastChangeSet);
        }
    }

    public onServerChangesReceived(callback: ChangesCallback): () => void {
        const key = uuid();
        this.serverChangesCallbacks[key] = callback;
        return () => delete this.serverChangesCallbacks[key];
    }

    public __testOnly__getChangeSetToSend(): ChangeSet {
        return this.changeSet;
    }

    public setCurrentDataVersion(version: number): void {
        this.currentDataVersion = version;
        this.isInConflictState = false;
    }

    private enqueueChangeSetToSend(changeSetOrAction: ChangeSet | InnerChangingAction): Promise<void> {
        const currentChangeSendDeferred = new Deferred<void>();
        this.changeSetSendQueue.push([changeSetOrAction, currentChangeSendDeferred]);
        return currentChangeSendDeferred.promise;
    }

    private dequeueChangeSetToSend(): [ChangeSet | InnerChangingAction, Deferred<void>] | undefined {
        return this.changeSetSendQueue.shift();
    }

    private getCurrentChangeSet(): ChangeSet {
        return IterUtils.last(this.redefinedChangeSets) ?? this.changeSet;
    }

    private setSavingStatus(value: FormSavingStatus): void {
        this.externalEventManager.triggerExternalEvent(formSavingStatusEvent(value));
        this.getStore().dispatch(setFormSaveStatus(value));
    }

    private setConflictState(): void {
        this.isInConflictState = true;
        this.onConflictDetected();
    }

    private async processEnqueuedChangeSets(force: boolean = false, operationName?: string): Promise<void> {
        const queueItem = this.dequeueChangeSetToSend();
        if (queueItem == undefined) {
            return;
        }

        this.processingRequest = true;
        try {
            const [changeSetOrAction, deferred] = queueItem;

            const changeSet = changeSetOrAction instanceof ChangeSet ? changeSetOrAction : undefined;

            if (changeSet != undefined && changeSet.isEmpty()) {
                deferred.resolve();
                return;
            }

            this.setSavingStatus("saving");
            try {
                const serverChangeSet = (
                    changeSet ?? new ChangeSet(this.useServerSideCalculations)
                ).toServerChangeSet();

                const action: InnerChangingAction =
                    changeSetOrAction instanceof ChangeSet
                        ? async () => {
                              const result = this.useServerSideCalculations
                                  ? await this.sendChangeWithNormalizationSet(
                                        serverChangeSet,
                                        this.currentDataVersion,
                                        undefined,
                                        operationName
                                    )
                                  : await this.candyApiDraftClient.sendChangeSet(
                                        serverChangeSet,
                                        this.currentDataVersion,
                                        operationName
                                    );
                              return result;
                          }
                        : changeSetOrAction;

                const startTime = Date.now();
                const result = await action();
                if (this.useServerSideCalculations) {
                    const requestOnlyDuration = Date.now() - startTime;
                    this.tracker.track(SSC_REQUEST_EXECUTED, {
                        duration: requestOnlyDuration,
                    });
                }
                if (typeof result === "object" && "dataVersion" in result) {
                    this.setCurrentDataVersion(result.dataVersion);
                    if (result.changedPaths != undefined && result.changedPaths.length > 0) {
                        for (const changesCallback of Object.values(this.serverChangesCallbacks)) {
                            await changesCallback(
                                serverChangeSet,
                                this.changeSetSendQueue
                                    .map(([x, _]) => (x instanceof ChangeSet ? x : undefined))
                                    .filter(isNotNullOrUndefined)
                                    .concat(this.changeSet),
                                result.changedPaths.map(x => AbsoluteModelFieldPath.createFromMask(x, PathTokens.each))
                            );
                        }
                    }
                } else if (typeof result === "number" && result === 409) {
                    this.setConflictState();
                    throw new ConflictChangeSetError("Conflict detected!");
                } else {
                    throw new Error("Unknown response type!");
                }

                if (this.useServerSideCalculations) {
                    const roundTripDuration = Date.now() - startTime;
                    this.tracker.track(SSC_REQUEST_WITH_CALLBACK_DATA_EXECUTED, {
                        duration: roundTripDuration,
                    });
                }
                deferred.resolve();
                this.setSavingStatus("success");
            } catch (error) {
                if (changeSet != undefined) {
                    const nextChangeSetInQueue = this.changeSetSendQueue
                        .map(([x, _]) => (x instanceof ChangeSet ? x : undefined))
                        .filter(isNotNullOrUndefined)[0];
                    const nextChangeSet = nextChangeSetInQueue != undefined ? nextChangeSetInQueue : this.changeSet;
                    nextChangeSet.prependWithChangeSet(changeSet);
                }
                deferred.reject(error);
                this.setSavingStatus(force ? "error" : "unsavedChanges");
            }
        } finally {
            this.processingRequest = false;
            runFloatingAsyncAction(() => this.processEnqueuedChangeSets());
        }
    }

    private async sendChangeWithNormalizationSet(
        serverChangeSet: ServerChangeSet,
        currentDataVersion: number,
        normalizerId?: string,
        operationName?: string
    ): Promise<IInnerChangeResult> {
        const pollingTask = await this.candyApiDraftClient.startPatchWithNormalizationTask(
            serverChangeSet,
            currentDataVersion,
            normalizerId,
            operationName
        );
        if (typeof pollingTask === "number") {
            return pollingTask;
        }
        if (pollingTask.status === "Success") {
            if (pollingTask.result === undefined) {
                throw new Error(`Task ${pollingTask.id} was finished successfully but result was undefined`);
            }
            return pollingTask.result;
        }
        const delaysToWait = [50, 100, 1000, 5000];
        const delaysCountLimits = [10, 20, 60];
        return await waitPollingTask(
            () => this.candyApiDraftClient.getPatchWithNormalizationTask(),
            undefined,
            delaysToWait,
            delaysCountLimits
        );
    }
}
